import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { ConciergePanel } from "@/components/concierge/ConciergePanel";
import { VISTACION_URL, CONTACT } from "@/constants";

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    if (!sessionId) return setState({ status: "error" });
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const d = await api(`/payments/status/${sessionId}`);
        if (d.payment_status === "paid") return setState({ status: "paid", ...d });
        if (["expired", "failed"].includes(d.payment_status)) return setState({ status: "failed" });
      } catch { /* keep polling */ }
      if (++attempts < 8) timer = setTimeout(poll, 2000);
      else setState({ status: "pending" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <PageShell>
      <section className="pt-40 pb-32 min-h-screen flex items-center" data-testid="payment-success-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          {state.status === "checking" && <p className="text-white/60" data-testid="payment-checking">Confirming your payment...</p>}
          {state.status === "paid" && state.kind === "deposit" && (<>
            <div className="w-14 h-14 mx-auto rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center text-gold mb-8"><Check size={22} /></div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Deposit received</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-white" data-testid="deposit-success-heading">Thank you{state.name ? `, ${state.name.split(" ")[0]}` : ""}.</h1>
            <p className="mt-6 text-white/60 leading-relaxed">Your deposit for <span className="text-white">{state.description}</span> has been received. A receipt is on its way and Marcelle will confirm the next steps shortly.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a href={CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-gold text-black text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:bg-gold-light transition-colors" data-testid="deposit-success-whatsapp-link">WhatsApp the team</a>
              <Link to="/" className="border border-white/20 text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="deposit-success-home-link">Back to the Club</Link>
            </div>
          </>)}
          {state.status === "paid" && state.kind === "jet_account" && (<>
            <div className="w-14 h-14 mx-auto rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center text-gold mb-8"><Check size={22} /></div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Jet Account funded</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-white" data-testid="jet-account-success-heading">Your flight balance is ready{state.name ? `, ${state.name.split(" ")[0]}` : ""}.</h1>
            <p className="mt-6 text-white/60 leading-relaxed">{new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format((state.amount || 0) / 100)} is now held on your Aero Vista Jet Account. A confirmation is on its way — message Marcelle with your first route whenever you are ready.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a href={CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-gold text-black text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:bg-gold-light transition-colors" data-testid="jet-account-success-whatsapp-link">WhatsApp Marcelle</a>
              <Link to="/" className="border border-white/20 text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="jet-account-success-home-link">Back to the Club</Link>
            </div>
          </>)}
          {state.status === "paid" && !["deposit", "jet_account"].includes(state.kind) && (<>
            <div className="w-14 h-14 mx-auto rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center text-gold mb-8"><Check size={22} /></div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Welcome to the Club</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-white" data-testid="payment-success-heading">Welcome aboard{state.name ? `, ${state.name.split(" ")[0]}` : ""}.</h1>
            <p className="mt-6 text-white/60 leading-relaxed">Your {state.plan?.toLowerCase()} is active. A welcome email is on its way and Marcelle will be in touch personally. In the meantime, explore the Vistacion member dashboard.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a href={VISTACION_URL} target="_blank" rel="noopener noreferrer" data-testid="payment-success-vistacion-link" className="bg-gold text-black text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:bg-gold-light transition-colors">Open Vistacion</a>
              <Link to="/" className="border border-white/20 text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="payment-success-home-link">Back to the Club</Link>
            </div>
          </>)}
          {state.status === "pending" && <p className="text-white/60" data-testid="payment-pending">Your payment is being confirmed. You will receive an email shortly; if not, contact us on {CONTACT.whatsappDisplay}.</p>}
          {(state.status === "failed" || state.status === "error") && <p className="text-white/60" data-testid="payment-failed">We could not confirm this payment. <Link to="/#membership" className="text-gold">Try again</Link> or contact us on {CONTACT.whatsappDisplay}.</p>}
        </div>
      </section>
    </PageShell>
  );
}

export function PaymentCancelPage() {
  return (
    <PageShell>
      <section className="pt-40 pb-32 min-h-screen flex items-center" data-testid="payment-cancel-page">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-white/5 border border-white/20 flex items-center justify-center text-white/60 mb-8"><X size={22} /></div>
          <h1 className="font-serif font-light text-4xl text-white">No payment was taken.</h1>
          <p className="mt-6 text-white/60">You can return to membership whenever you are ready, or speak to the team first.</p>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/#membership" className="bg-gold text-black text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:bg-gold-light transition-colors" data-testid="payment-cancel-retry-link">Back to membership</Link>
            <a href={CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer" className="border border-white/20 text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:border-gold hover:text-gold transition-colors" data-testid="payment-cancel-whatsapp-link">WhatsApp the team</a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

export function PayDepositPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api(`/payments/deposit/${id}`).then(setD).catch(() => setError(true)); }, [id]);
  const amount = d ? new Intl.NumberFormat("en-GB", { style: "currency", currency: (d.currency || "gbp").toUpperCase() }).format(d.amount / 100) : "";
  return (
    <PageShell>
      <section className="pt-40 pb-32 min-h-screen flex items-center" data-testid="pay-deposit-page">
        <div className="max-w-lg mx-auto px-6 w-full">
          {error && <p className="text-white/60 text-center" data-testid="pay-not-found">This payment link is no longer valid. Please contact the team on {CONTACT.whatsappDisplay}.</p>}
          {d && (
            <div className="bg-surface border border-gold/40 rounded-sm p-10">
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{d.kind === "jet_account" ? "Jet Account top-up" : "Secure deposit"}</p>
              <h1 className="font-serif font-light text-3xl sm:text-4xl text-white" data-testid="pay-description">{d.description}</h1>
              <p className="mt-2 text-sm text-white/50">For {d.name}</p>
              <p className="font-serif text-5xl text-gold mt-8" data-testid="pay-amount">{amount}</p>
              {d.payment_status === "paid" ? (
                <p className="mt-8 inline-flex items-center gap-2 text-emerald-300 text-sm" data-testid="pay-already-paid"><Check size={16} /> This payment has been received. Thank you.</p>
              ) : (
                <a href={d.checkout_url} data-testid="pay-stripe-button" className="mt-8 w-full inline-flex items-center justify-center bg-gold text-black text-xs uppercase tracking-[0.2em] py-4 rounded-sm hover:bg-gold-light transition-colors">Pay securely with Stripe</a>
              )}
              <p className="mt-6 text-[11px] text-white/30">Payments are processed by Stripe. Aero Vista Club never asks for card details by email or phone.</p>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}

export function EmbedConciergePage() {
  const [params] = useSearchParams();
  const source = params.get("source") || "vistacion";
  return (
    <div className="h-screen w-screen bg-obsidian" data-testid="embed-concierge-page">
      <ConciergePanel source={source} className="h-full" />
    </div>
  );
}
